/** DRW-LINKS — one way out of a grid and onto the paper.
 *
 *  Every drawing link in the app goes through here, so there is a single answer to "which tab does
 *  this open in", a single URL shape, and a single way back. The interstitial in RA is gone.
 *
 *  URL: /drawings?project=&sheet=&room=&product=&markup=&from=&vs=&back=1
 *   - from  = the view the user left (install | material | picking | frs | ra | dashboard)
 *   - vs    = one-shot token naming the snapshot of that view's filter state
 *   - back  = 1 when we navigated in place, so the drawing shows the Back bar
 */

const PREF = "fieldops-drawing-link-target";      // 'new' (default) | 'same'

export const linkTarget = () => (localStorage.getItem(PREF) === "same" ? "same" : "new");
export const setLinkTarget = (v) => localStorage.setItem(PREF, v === "same" ? "same" : "new");

/** A handheld has no second tab to come back to: narrow, or no hover (phone / tablet PWA). */
export const isHandheld = () => {
  if (typeof window === "undefined") return false;
  const noHover = typeof window.matchMedia === "function" && !window.matchMedia("(hover: hover)").matches;
  return window.innerWidth < 1024 || noHover;
};

export const ORIGIN_LABELS = {
  install: "Install", material: "Material", picking: "Picking", delivery: "Delivery",
  frs: "FRS", ra: "Project", dashboard: "Dashboard",
};

export const drawingUrl = ({ project, sheet = "", sheetId = "", room = "", product = "", markup = "",
                             from = "", vs = "", back = false, scopes = [] }) => {
  const q = new URLSearchParams({ project });
  if (scopes && scopes.length) q.set("scopes", scopes.join(","));   // DRW-B §4 — the FRS scope filter rides along
  if (sheet) q.set("sheet", sheet);
  if (sheetId) q.set("sheet_id", sheetId);           // DRW-6 — a field shop sheet, opened directly by id
  if (room) q.set("room", String(room));
  if (product) q.set("product", product);
  if (markup) q.set("markup", markup);
  if (from) q.set("from", from);
  if (vs) q.set("vs", vs);
  if (back) q.set("back", "1");
  return `/drawings?${q.toString()}`;
};

/** Where a drawing link came from, so Back and "Open in FRS" land on the same view.
 *  Install, Material and Picking are three modes of one route — the mode itself comes back out of
 *  the remembered state, not out of the URL. */
export const originPath = ({ from, project, room = "", product = "", vs = "", scopes = [] }) => {
  const q = new URLSearchParams();
  if (scopes && scopes.length) q.set("scopes", scopes.join(","));
  if (room) q.set("room", String(room));
  if (product) q.set("product", product);
  if (vs) q.set("vs", vs);
  if (["install", "material", "picking", "delivery"].includes(from)) {
    q.set("project", project);
    return `/install?${q.toString()}`;
  }
  if (from === "dashboard") return "/dashboard";
  q.set("tab", "frs");
  return `/projects/${project}?${q.toString()}`;
};

/** Desktop opens a new tab by default so the grid stays exactly where it was; a handheld, or the
 *  "same tab" preference, opens in place and gets the Back bar. Returns 'new' or 'same'. */
export function openDrawing(navigate, params) {
  const newTab = !isHandheld() && linkTarget() === "new";
  if (newTab) {
    const w = window.open(drawingUrl({ ...params, back: false }), "_blank", "noopener");
    if (w) return "new";                      // blocked by the browser → fall through, never dead
  }
  navigate(drawingUrl({ ...params, back: true }));
  return "same";
}
